import { Logo } from '../../../js/app/components/Logo';
import { Button } from './button';
import { Dialog, DialogCloseButton } from './dialog';
import { Text } from './text';

interface HowItWorksDialogProps {
  open: boolean;
  onClose: () => void;
}

const ADVANTAGE_CARDS = [
  {
    icon: 'far fa-search',
    title: 'Full transparency, upfront',
    description:
      'See the hospital details, pay, and terms before you express interest. No vague job pitches or surprises later.',
  },
  {
    icon: 'far fa-calendar',
    title: 'Work on your terms',
    description:
      'Choose your own dates, your own rates, and the assignments that fit your life — not the other way around.',
  },
  {
    icon: 'far fa-phone',
    title: 'White-glove logistics support',
    description:
      'Credentialing, PLI, and travel are all handled for you by a team that communicates and actually follows through.',
  },
  {
    icon: 'far fa-users',
    title: 'A team that respects your time',
    description:
      'No spam calls. No pressure. Just clear opportunities and a team that respects your time and professionalism.',
  },
];

export function HowItWorksDialog({ open, onClose }: HowItWorksDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} size="2xl" align="top">
      <div className="tw:flex tw:flex-col tw:max-h-[85vh] tw:overflow-hidden">
        {/* Header */}
        <div className="tw:flex tw:items-start tw:justify-between tw:gap-4 tw:shrink-0">
          <div className="tw:flex tw:items-center tw:gap-2">
            <Logo className="tw:h-8" variant="color" />
          </div>
          <DialogCloseButton onClick={onClose} className="tw:shrink-0" />
        </div>

        {/* Scrollable content */}
        <div className="tw:flex-1 tw:overflow-y-auto tw:mt-6">
          <h4 className="tw:!font-bold">Work with Hospitals.</h4>
          <h4 className="tw:!font-bold tw:!text-brand-green-0">Not recruiters.</h4>
          <Text className="tw:mt-2 tw:!mb-0 tw:!text-sm">
            A better way for physicians to get contract work, connecting you directly through a
            platform built for transparency and control.
          </Text>

          {/* Advantage cards */}
          <div className="tw:mt-4 tw:space-y-3">
            {ADVANTAGE_CARDS.map((card) => (
              <div
                key={card.title}
                className="tw:flex tw:gap-4 tw:p-4 tw:rounded-lg tw:bg-zinc-100"
              >
                <div className="tw:shrink-0 tw:w-10 tw:h-10 tw:rounded-lg tw:bg-brand-cta tw:flex tw:items-center tw:justify-center">
                  <i className={`${card.icon} tw:text-white tw:text-lg`} />
                </div>
                <div>
                  <p className="tw:!mb-0 tw:font-semibold">{card.title}</p>
                  <Text className="tw:mt-1 tw:!mb-0 tw:!text-sm">{card.description}</Text>
                </div>
              </div>
            ))}
          </div>

          {/* Quote block */}
          <div className="tw:mt-4 tw:pl-4 tw:border-l-4 tw:border-brand-green-0 tw:bg-brand-green-3 tw:rounded-r-lg tw:py-4 tw:pr-4">
            <blockquote className="tw:text-zinc-700 tw:dark:text-zinc-300 tw:italic tw:!mb-2">
              &ldquo;Unlike agencies, physician autonomy and value as a contractor is part of their
              ethos. I have had the highest return on investment for time spent.&rdquo;
            </blockquote>
            <div className="tw:flex tw:gap-1 tw:mb-2">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="fas fa-star tw:text-brand-cta" />
              ))}
            </div>
            <Text className="tw:!mb-0 tw:!text-sm">
              Dr. Beg · Verified physician review · Google
            </Text>
          </div>

          {/* CTA and footer */}
          <div className="tw:mt-4 tw:space-y-4">
            <Button color="brandgreen1" className="tw:!w-full" onClick={onClose}>
              Join Sendit — It&apos;s Free
            </Button>
            <Text className="tw:!text-sm tw:!mt-2 tw:!mb-0">
              No commitment. Sign up and complete your profile and we&apos;ll notify you when
              there&apos;s a match.
            </Text>
            <Text className="tw:!text-sm tw:!mb-0">
              Still have questions?{' '}
              <a
                href="https://www.senditgigs.com"
                target="_blank"
                rel="noopener noreferrer"
                className="tw:text-brand-green-0 tw:underline tw:hover:text-brand-green-1"
              >
                Visit our website
              </a>
              .
            </Text>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
